/**
 * Reading and writing what is owed on a deal.
 *
 * Kept out of the component so the dashboard and the deal page cannot
 * disagree about what "open" means.
 */

import { supabase } from "../../lib/supabase";
import { buildWeOwe, type WeOwePromise } from "../../forms/weOwe";
import { loadDealContext } from "./buildPacket";
import { nothingChanged } from "../../lib/saving";

export type WeOweStatus = "open" | "completed" | "cancelled";

export interface WeOwe {
  id: string;
  deal_id: string;
  title: string;
  body: string;
  due_date: string | null;
  status: WeOweStatus;
  completed_on: string | null;
  completion_note: string | null;
  created_at: string;
}

/** A promise still outstanding, with enough of its deal attached to act on
    it. Comes from the open_we_owes view, which does the join in the database
    so a report and the dashboard cannot describe it differently. */
export interface OpenWeOwe {
  id: string;
  deal_id: string;
  title: string;
  due_date: string | null;
  deal_no: string;
  buyer_name: string;
  vehicle: string;
  vin: string;
  overdue: boolean;
}

const COLUMNS =
  "id,deal_id,title,body,due_date,status,completed_on,completion_note,created_at";

export async function listWeOwes(dealId: string): Promise<WeOwe[]> {
  const { data, error } = await supabase
    .from("we_owes")
    .select(COLUMNS)
    .eq("deal_id", dealId)
    // Open first, then by promised date: the thing with a date on it this
    // week matters more than the thing with no date at all.
    .order("status")
    .order("due_date", { ascending: true, nullsFirst: false })
    .order("created_at");
  if (error) throw new Error(`Could not load what is owed: ${error.message}`);
  return (data ?? []) as WeOwe[];
}

export async function addWeOwe(
  dealId: string,
  fields: { title: string; body: string; dueDate: string | null },
): Promise<WeOwe> {
  const { data, error } = await supabase
    .from("we_owes")
    .insert({
      deal_id: dealId,
      title: fields.title.trim(),
      body: fields.body.trim(),
      due_date: fields.dueDate || null,
    })
    .select(COLUMNS)
    .single();
  if (error || !data) {
    throw new Error(`Could not save it: ${error?.message ?? "nothing came back"}`);
  }
  return data as WeOwe;
}

export async function updateWeOwe(
  id: string,
  fields: { title: string; body: string; dueDate: string | null },
): Promise<void> {
  const { data, error } = await supabase
    .from("we_owes")
    .update({
      title: fields.title.trim(),
      body: fields.body.trim(),
      due_date: fields.dueDate || null,
    })
    .eq("id", id)
    .select("id");
  if (error) throw new Error(`Could not save the change: ${error.message}`);
  if (!data?.length) throw nothingChanged();
}

/**
 * Close a promise out. The completion date is required by a check constraint
 * on the table — a completed row with no date is the kind of record that
 * looks fine until somebody asks when the work was actually done.
 */
export async function completeWeOwe(
  id: string,
  completedOn: string,
  note: string,
): Promise<void> {
  const { data, error } = await supabase
    .from("we_owes")
    .update({
      status: "completed",
      completed_on: completedOn,
      completion_note: note.trim() || null,
    })
    .eq("id", id)
    .select("id");
  if (error) throw new Error(`Could not mark it done: ${error.message}`);
  if (!data?.length) throw nothingChanged();
}

/** Put a closed promise back on the list. The date has to be cleared in the
    same statement or the constraint refuses the row. */
export async function reopenWeOwe(id: string): Promise<void> {
  const { data, error } = await supabase
    .from("we_owes")
    .update({ status: "open", completed_on: null, completion_note: null })
    .eq("id", id)
    .select("id");
  if (error) throw new Error(`Could not reopen it: ${error.message}`);
  if (!data?.length) throw nothingChanged();
}

export async function deleteWeOwe(id: string): Promise<void> {
  const { data, error } = await supabase.from("we_owes").delete().eq("id", id).select("id");
  if (error) throw new Error(`Could not delete it: ${error.message}`);
  if (!data?.length) throw nothingChanged();
}

/** Everything still owed across the lot, soonest first. */
export async function listOpenWeOwes(): Promise<OpenWeOwe[]> {
  const { data, error } = await supabase
    .from("open_we_owes")
    .select("id,deal_id,title,due_date,deal_no,buyer_name,vehicle,vin,overdue")
    .order("due_date", { ascending: true, nullsFirst: false });
  if (error) throw new Error(`Could not load outstanding promises: ${error.message}`);
  return (data ?? []) as OpenWeOwe[];
}

/**
 * Build the printable page for a deal.
 *
 * Only open promises print. A page that lists work already done reads as
 * though it is still owed, and the buyer keeps his copy: handing him a fresh
 * one that re-promises last month's finished job is how a closed item gets
 * argued open again.
 */
export async function buildWeOweForDeal(dealId: string): Promise<Uint8Array> {
  const ctx = await loadDealContext(dealId);
  const owed = (await listWeOwes(dealId)).filter((w) => w.status === "open");
  const promises: WeOwePromise[] = owed.map((w) => ({
    title: w.title,
    body: w.body,
    dueDate: w.due_date,
  }));
  return buildWeOwe(ctx, promises);
}
